import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown, Menu, X, Mail, LifeBuoy, Shield, Copyright,
  ArrowUpRight, Clock,
} from "lucide-react";

/**
 * LegalPage — polished, section-driven layout used by every legal / info page.
 *
 * Props
 *   icon                   Lucide icon component (hero + tab-icon)
 *   label                  Small kicker above the hero title (e.g. "LEGAL")
 *   title                  Big hero title
 *   intro                  Branded 1-2 sentence description under the title
 *   updated                Human-readable last-updated string
 *   sections               Array of { id, title, icon?, highlight? ("volt"|"pink"|"cyan"|"orange"), body: JSX }
 *   trustCard              Optional { title, points: string[] } — renders as a premium highlighted card
 *   contact                Optional { title, body, cta: {label, to} }
 */
export default function LegalPage({
  icon: HeroIcon,
  label,
  title,
  intro,
  updated,
  sections = [],
  trustCard = null,
  contact = null,
}) {
  const [activeId, setActiveId] = useState(sections[0]?.id || "");
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const observerRef = useRef(null);

  // Scroll observer to highlight the active TOC entry
  useEffect(() => {
    if (!sections.length) return;
    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveId(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observerRef.current.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, [sections]);

  // Reveal-on-scroll
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in")),
      { threshold: 0.1 }
    );
    document.querySelectorAll("[data-reveal]").forEach((el) => io.observe(el));
    window.scrollTo(0, 0);
    return () => io.disconnect();
  }, [sections.length]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
      setMobileTocOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] relative overflow-hidden" data-testid="legal-page">
      {/* Backdrop */}
      <div className="neon-orb" style={{ background: "#CCFF00", width: 260, height: 260, top: -80, right: "5%" }} />
      <div className="neon-orb" style={{ background: "#FF1053", width: 320, height: 320, top: 200, left: -100, animationDelay: "-3s" }} />
      <div className="cyber-grid opacity-25" />

      {/* ============ HERO ============ */}
      <header className="relative z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-12 reveal in" data-reveal>
          <div className="flex items-start gap-6">
            {HeroIcon && (
              <div className="hidden sm:flex flex-shrink-0 w-16 h-16 rounded-md items-center justify-center border border-[color:var(--sc-volt)]/40 bg-[color:var(--sc-volt)]/5 anim-pulse-volt">
                <HeroIcon className="w-8 h-8 text-[color:var(--sc-volt)]" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              {label && <div className="sc-label mb-3" style={{ color: "var(--sc-orange)" }}>◢ {label}</div>}
              <h1 className="pixel-title text-4xl sm:text-5xl lg:text-6xl leading-none">{title}</h1>
              {intro && (
                <p className="text-zinc-300 text-base sm:text-lg mt-5 max-w-3xl leading-relaxed">
                  {intro}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-3 mt-6 text-[10px] sc-mono">
                {updated && (
                  <span className="chip-neon cyan">
                    <Clock className="w-2.5 h-2.5" /> LAST UPDATED · {updated.toUpperCase()}
                  </span>
                )}
                <span className="chip-neon volt">READ TIME · ~{Math.max(2, Math.round(sections.length * 1.5))} MIN</span>
                <span className="chip-neon pink">MYSTICAST · OFFICIAL</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ============ BODY ============ */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid lg:grid-cols-[240px_minmax(0,1fr)] gap-8 lg:gap-12">
          {/* Sticky sidebar TOC (desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <div className="sc-label text-[9px] mb-3" style={{ color: "var(--sc-cyan)" }}>◈ ON THIS PAGE</div>
              <nav className="space-y-1" data-testid="legal-toc">
                {sections.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className={`w-full text-left px-3 py-2 rounded-sm text-[13px] transition border-l-2 ${
                      activeId === s.id
                        ? "border-[color:var(--sc-volt)] bg-[color:var(--sc-volt)]/10 text-white"
                        : "border-transparent text-zinc-500 hover:text-white hover:border-white/20 hover:bg-white/5"
                    }`}
                    data-testid={`legal-toc-${s.id}`}
                  >
                    <span className="sc-mono text-[10px] text-zinc-600 mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {s.title}
                  </button>
                ))}
              </nav>
              <div className="mt-6 pt-4 border-t border-white/5 space-y-1 text-[11px]">
                <Link to="/contact" className="flex items-center gap-2 text-zinc-500 hover:text-[color:var(--sc-cyan)]">
                  <Mail className="w-3 h-3" /> Have a question?
                </Link>
                <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2 text-zinc-500 hover:text-white">
                  <ArrowUpRight className="w-3 h-3 rotate-[-90deg]" /> Back to top
                </button>
              </div>
            </div>
          </aside>

          {/* Mobile TOC toggle */}
          <div className="lg:hidden sticky top-16 z-30 -mx-6 px-6 py-3 bg-[#0A0A0C]/95 backdrop-blur border-b border-white/5">
            <button
              onClick={() => setMobileTocOpen(!mobileTocOpen)}
              className="flex items-center justify-between w-full text-left"
              data-testid="legal-toc-mobile-toggle"
            >
              <div>
                <div className="sc-label text-[9px]" style={{ color: "var(--sc-cyan)" }}>ON THIS PAGE</div>
                <div className="text-sm text-white truncate mt-0.5">
                  {sections.find((s) => s.id === activeId)?.title || sections[0]?.title}
                </div>
              </div>
              {mobileTocOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
            {mobileTocOpen && (
              <div className="mt-3 pt-3 border-t border-white/5 space-y-1 max-h-[60vh] overflow-y-auto">
                {sections.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className={`w-full text-left px-3 py-2 rounded-sm text-sm ${
                      activeId === s.id ? "bg-[color:var(--sc-volt)]/15 text-white" : "text-zinc-400 hover:bg-white/5"
                    }`}
                  >
                    <span className="sc-mono text-[10px] text-zinc-600 mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {s.title}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Main body */}
          <main className="max-w-3xl">
            {/* Trust card (optional, before sections) */}
            {trustCard && (
              <div
                data-reveal
                data-testid="trust-card"
                className="reveal relative mb-10 rounded-lg border border-[color:var(--sc-volt)]/40 bg-gradient-to-br from-[color:var(--sc-volt)]/10 to-transparent p-6 sm:p-8 overflow-hidden"
              >
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl" style={{ background: "#CCFF00", opacity: 0.12 }} />
                <div className="relative">
                  <div className="sc-label" style={{ color: "var(--sc-volt)" }}>◢ {trustCard.kicker || "OUR COMMITMENT"}</div>
                  <h2 className="sc-heading text-2xl sm:text-3xl font-black text-white mt-2">{trustCard.title}</h2>
                  <ul className="mt-5 space-y-3">
                    {(trustCard.points || []).map((p, i) => (
                      <li key={i} className="flex gap-3 text-sm sm:text-[15px] text-zinc-200 leading-relaxed">
                        <span className="text-[color:var(--sc-volt)] font-bold mt-0.5">▸</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Sections */}
            <div className="prose-mysticast">
              {sections.map((s, i) => {
                const highlight = s.highlight;
                return (
                  <section
                    key={s.id}
                    id={s.id}
                    data-reveal
                    className={`reveal scroll-mt-24 pb-10 mb-10 border-b border-white/5 last:border-0 ${
                      highlight ? "relative pl-5 sm:pl-6" : ""
                    }`}
                    data-testid={`legal-section-${s.id}`}
                  >
                    {highlight && (
                      <span
                        aria-hidden
                        className="absolute left-0 top-4 bottom-4 w-[3px] rounded"
                        style={{ background: `var(--sc-${highlight})`, boxShadow: `0 0 10px var(--sc-${highlight})` }}
                      />
                    )}
                    <div className="flex items-center gap-3 mb-4">
                      <span className="sc-mono text-[10px] text-zinc-600">{String(i + 1).padStart(2, "0")}</span>
                      {s.icon && (
                        <span className="w-8 h-8 rounded flex items-center justify-center border border-white/10 bg-white/5">
                          <s.icon className="w-4 h-4 text-[color:var(--sc-volt)]" />
                        </span>
                      )}
                      <h2 className="sc-heading text-2xl sm:text-3xl font-black text-white leading-tight m-0">
                        {s.title}
                      </h2>
                    </div>
                    <div>{s.body}</div>
                  </section>
                );
              })}
            </div>

            {/* Contact card */}
            <ContactCard contact={contact} />

            {/* Legal sub-footer */}
            <LegalSubFooter />
          </main>
        </div>
      </div>

      {/* Prose typography */}
      <style>{`
        .prose-mysticast h3 {
          font-family: "Unbounded", sans-serif;
          font-weight: 700;
          font-size: 1.05rem;
          color: #f4f4f5;
          margin: 1.5rem 0 0.5rem;
          letter-spacing: -0.01em;
        }
        .prose-mysticast p, .prose-mysticast li {
          color: #d4d4d8;
          line-height: 1.75;
          font-size: 0.98rem;
          max-width: 68ch;
        }
        .prose-mysticast p { margin-bottom: 1rem; }
        .prose-mysticast ul { padding-left: 0; list-style: none; margin: 0.5rem 0 1rem; }
        .prose-mysticast ul li { position: relative; padding-left: 1.5rem; margin-bottom: 0.6rem; }
        .prose-mysticast ul li::before {
          content: "▸";
          position: absolute;
          left: 0;
          color: var(--sc-cyan);
          font-weight: 700;
        }
        .prose-mysticast strong { color: #ffffff; }
        .prose-mysticast a {
          color: var(--sc-cyan);
          text-decoration: underline;
          text-underline-offset: 3px;
          text-decoration-color: rgba(0, 240, 255, 0.4);
          transition: text-decoration-color 0.2s ease;
        }
        .prose-mysticast a:hover { text-decoration-color: var(--sc-cyan); }
        .prose-mysticast code {
          background: rgba(255,255,255,0.06);
          color: var(--sc-volt);
          padding: 1px 6px;
          border-radius: 3px;
          font-family: "IBM Plex Mono", monospace;
          font-size: 0.85em;
        }
        .prose-mysticast .info-card {
          border: 1px solid rgba(255,255,255,0.08);
          background: rgba(255,255,255,0.02);
          border-radius: 6px;
          padding: 1.1rem 1.25rem;
          margin: 1rem 0;
          transition: border-color .2s ease, transform .2s ease;
        }
        .prose-mysticast .info-card:hover {
          border-color: rgba(204,255,0,0.4);
          transform: translateY(-1px);
        }
        .prose-mysticast .info-card .info-card-head {
          display: flex; align-items: center; gap: 0.6rem;
          font-family: "Unbounded", sans-serif;
          font-weight: 800;
          font-size: 0.95rem;
          color: #fff;
          margin-bottom: 0.5rem;
        }
        .prose-mysticast .info-card p:last-child { margin-bottom: 0; }

        /* Focus outlines for keyboard nav */
        .prose-mysticast a:focus-visible,
        [data-testid^="legal-toc-"]:focus-visible {
          outline: 2px solid var(--sc-volt);
          outline-offset: 3px;
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
}

// ------------------------------------------------------
function ContactCard({ contact }) {
  const c = contact || {
    title: "Questions about this document?",
    body: "The MystiCast Support Team is here to help. We reply to every message.",
    cta: { label: "Contact Support", to: "/contact" },
  };
  return (
    <div
      data-reveal
      data-testid="legal-contact-card"
      className="reveal rounded-lg border border-white/10 bg-gradient-to-br from-white/5 to-transparent p-6 sm:p-8 mt-6"
    >
      <div className="flex items-start gap-4">
        <div className="hidden sm:flex w-11 h-11 rounded-md items-center justify-center border border-[color:var(--sc-cyan)]/40 bg-[color:var(--sc-cyan)]/5">
          <LifeBuoy className="w-5 h-5 text-[color:var(--sc-cyan)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="sc-heading text-xl font-black text-white">{c.title}</div>
          <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{c.body}</p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {c.cta.to.startsWith("mailto:") || c.cta.to.startsWith("http") ? (
              <a href={c.cta.to} className="sc-btn text-xs flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> {c.cta.label}
              </a>
            ) : (
              <Link to={c.cta.to} className="sc-btn text-xs flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> {c.cta.label}
              </Link>
            )}
            <Link to="/safety" className="sc-btn-ghost text-xs flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" /> Trust & Safety
            </Link>
            <Link to="/dmca" className="sc-btn-ghost text-xs flex items-center gap-1.5">
              <Copyright className="w-3.5 h-3.5" /> DMCA
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------
function LegalSubFooter() {
  const links = [
    ["Privacy Policy", "/privacy"],
    ["Terms of Service", "/terms"],
    ["Community Guidelines", "/community"],
    ["DMCA", "/dmca"],
    ["Cookie Policy", "/cookies"],
    ["Refunds & Payouts", "/refunds"],
    ["Trust & Safety", "/safety"],
    ["Contact", "/contact"],
  ];
  return (
    <div className="mt-10 pt-8 border-t border-white/5" data-testid="legal-sub-footer">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-zinc-500 sc-mono">
        {links.map(([l, to], i) => (
          <React.Fragment key={to}>
            {i > 0 && <span className="text-zinc-700">·</span>}
            <Link to={to} className="hover:text-[color:var(--sc-cyan)] transition">{l}</Link>
          </React.Fragment>
        ))}
      </div>
      <div className="text-[10px] text-zinc-600 mt-4 sc-mono">
        &copy; {new Date().getFullYear()} MystiCast &middot; Where creators build communities.
      </div>
    </div>
  );
}

// ------------------------------------------------------
// Reusable info-card for use in section bodies.
export function InfoCard({ icon: Icon, title, tone = "volt", children }) {
  return (
    <div className="info-card" data-testid={`info-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="info-card-head">
        {Icon && (
          <span
            className="w-6 h-6 rounded-sm flex items-center justify-center flex-shrink-0"
            style={{ background: `var(--sc-${tone})22`, color: `var(--sc-${tone})` }}
          >
            <Icon className="w-3.5 h-3.5" />
          </span>
        )}
        <span>{title}</span>
      </div>
      <div>{children}</div>
    </div>
  );
}

// Chevron sub-component (kept in module for future collapsible sections)
export { ChevronDown };
